import { API_CONFIG } from '@/lib/api/config';

// MySQL Database Client (via API)
class MySQLClient {
  private baseUrl: string;
  public auth: any;
  private authStateChangeCallback: ((event: string, session: any) => void) | null = null;

  // Table to REST API endpoint mapping
  private tableEndpointMap: Record<string, string> = {
    users: 'users',
    business_accounts: 'business-accounts',
    contacts: 'contacts',
    templates: 'templates',
    campaigns: 'campaigns',
    whatsapp_templates: 'whatsapp/templates',
    template_components: 'templates', // Components are nested under templates
    template_buttons: 'templates', // Buttons are nested under templates
    template_carousel_cards: 'templates', // Carousel cards are nested under templates
    template_carousel_card_buttons: 'templates', // Carousel card buttons are nested under templates
    whatsapp_business_accounts: 'business-accounts', // Same as business_accounts
    automation_flows: 'automation/flows',
    conversations: 'conversations',
    messages: 'messages',
    audit_logs: 'audit-logs',
    media_files: 'media',
    flow_executions: 'automation/flows', // Nested under automation flows
    campaign_recipients: 'campaigns', // Nested under campaigns
  };

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;

    // Mock auth object to maintain compatibility with Supabase client
    this.auth = {
      onAuthStateChange: (callback: (event: string, session: any) => void) => {
        // Store the callback for later use
        this.authStateChangeCallback = callback;

        // Mock implementation - return a subscription object
        const mockSession = {
          user: {
            id: 'mock-user-id',
            email: 'demo@example.com',
            user_metadata: {},
            app_metadata: {}
          },
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token'
        };

        // Simulate auth state change
        setTimeout(() => {
          callback('SIGNED_IN', mockSession);
        }, 100);

        return {
          data: {
            subscription: {
              unsubscribe: () => {
                this.authStateChangeCallback = null;
              }
            }
          }
        };
      },

      getSession: async () => {
        // Check if user is signed in by looking for auth tokens in localStorage
        const hasAuthToken = Object.keys(localStorage).some(key =>
          key.startsWith('supabase.auth.') || key.includes('sb-')
        );

        if (!hasAuthToken) {
          return {
            data: {
              session: null
            },
            error: null
          };
        }

        // Mock session for development
        const mockSession = {
          user: {
            id: 'mock-user-id',
            email: 'demo@example.com',
            user_metadata: {},
            app_metadata: {}
          },
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token'
        };

        return {
          data: {
            session: mockSession
          },
          error: null
        };
      },

      signOut: async (options?: any) => {
        // Mock sign out - clear localStorage and trigger auth state change

        // Clear all auth-related localStorage items
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
            localStorage.removeItem(key);
          }
        });

        // Clear sessionStorage as well
        Object.keys(sessionStorage || {}).forEach((key) => {
          if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
            sessionStorage.removeItem(key);
          }
        });

        // Trigger auth state change to SIGNED_OUT
        setTimeout(() => {
          if (this.authStateChangeCallback) {
            this.authStateChangeCallback('SIGNED_OUT', null);
          }
        }, 100);

        return { error: null };
      },

      signInWithPassword: async (credentials: any) => {
        // Mock sign in

        // Set a mock auth token in localStorage
        localStorage.setItem('supabase.auth.token', JSON.stringify({
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token',
          expires_at: Date.now() + 3600000, // 1 hour from now
          token_type: 'bearer',
          user: {
            id: 'mock-user-id',
            email: credentials.email,
            user_metadata: {},
            app_metadata: {}
          }
        }));

        const mockUser = {
          id: 'mock-user-id',
          email: credentials.email,
          user_metadata: {},
          app_metadata: {}
        };

        const mockSession = {
          user: mockUser,
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token'
        };

        // Trigger auth state change
        setTimeout(() => {
          if (this.authStateChangeCallback) {
            this.authStateChangeCallback('SIGNED_IN', mockSession);
          }
        }, 100);

        return {
          data: {
            user: mockUser,
            session: mockSession
          },
          error: null
        };
      },

      signUp: async (credentials: any) => {
        // Mock sign up

        // Set a mock auth token in localStorage
        localStorage.setItem('supabase.auth.token', JSON.stringify({
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token',
          expires_at: Date.now() + 3600000, // 1 hour from now
          token_type: 'bearer',
          user: {
            id: 'mock-user-id',
            email: credentials.email,
            user_metadata: credentials.options?.data || {},
            app_metadata: {}
          }
        }));

        const mockUser = {
          id: 'mock-user-id',
          email: credentials.email,
          user_metadata: credentials.options?.data || {},
          app_metadata: {}
        };

        const mockSession = {
          user: mockUser,
          access_token: 'mock-token',
          refresh_token: 'mock-refresh-token'
        };

        // Trigger auth state change
        setTimeout(() => {
          if (this.authStateChangeCallback) {
            this.authStateChangeCallback('SIGNED_IN', mockSession);
          }
        }, 100);

        return {
          data: {
            user: mockUser,
            session: mockSession
          },
          error: null
        };
      }
    };
  }

  // Helper method to make API requests
  private async apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<{ data: T | null; error: any }> {
    try {
      const token = typeof window !== 'undefined'
        ? (localStorage.getItem('auth_token') || localStorage.getItem('token'))
        : null;

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      // The API returns data directly, not wrapped in a data property
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Network error' };
    }
  }

  // Select data from a table
  from<T = any>(table: string) {
    return {
      select: (columns: string = '*') => {
        return {
          eq: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: value }),
          neq: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $neq: value } }),
          gt: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $gt: value } }),
          gte: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $gte: value } }),
          lt: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $lt: value } }),
          lte: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $lte: value } }),
          like: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $like: value } }),
          ilike: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $ilike: value } }),
          in: (column: string, values: any[]) => this.buildSelectQuery<T>(table, columns, { [column]: { $in: values } }),
          is: (column: string, value: any) => this.buildSelectQuery<T>(table, columns, { [column]: { $is: value } }),
          order: (column: string, options: { ascending?: boolean } = {}) => this.buildSelectQuery<T>(table, columns, {}, { orderBy: column, ascending: options.ascending !== false }),
          limit: (count: number) => this.buildSelectQuery<T>(table, columns, {}, { limit: count }),
          range: (from: number, to: number) => this.buildSelectQuery<T>(table, columns, {}, { limit: to - from + 1, offset: from }),
          single: () => this.buildSelectQuery<T>(table, columns, {}, { single: true }),
          maybeSingle: () => this.buildSelectQuery<T>(table, columns, {}, { single: true, allowEmpty: true }),
          then: async (resolve: (result: { data: T[] | T | null; error: any }) => void) => {
            const result = await this.buildSelectQuery<T>(table, columns, {});
            resolve(result);
            return result;
          }
        };
      },

      insert: (data: Partial<T> | Partial<T>[]) => {
        return {
          select: (columns: string = '*') => this.insertAndSelect<T>(table, data, columns),
          then: async (resolve: (result: { data: T[] | null; error: any }) => void) => {
            const result = await this.insertData<T>(table, data);
            resolve(result);
            return result;
          }
        };
      },

      update: (data: Partial<T>) => {
        return {
          eq: (column: string, value: any) => this.buildUpdateQuery<T>(table, data, { [column]: value }),
          neq: (column: string, value: any) => this.buildUpdateQuery<T>(table, data, { [column]: { $neq: value } }),
          select: (columns: string = '*') => this.buildUpdateQuery<T>(table, data, {}, columns),
          then: async (resolve: (result: { data: T[] | null; error: any }) => void) => {
            const result = await this.updateData<T>(table, data, {});
            resolve(result);
            return result;
          }
        };
      },

      delete: () => {
        return {
          eq: (column: string, value: any) => this.buildDeleteQuery<T>(table, { [column]: value }),
          neq: (column: string, value: any) => this.buildDeleteQuery<T>(table, { [column]: { $neq: value } }),
          then: async (resolve: (result: { data: T[] | null; error: any }) => void) => {
            const result = await this.deleteData<T>(table, {});
            resolve(result);
            return result;
          }
        };
      }
    };
  }

  // Helper method to build SELECT queries
  private async buildSelectQuery<T>(
    table: string,
    columns: string,
    filters: Record<string, any> = {},
    options: { orderBy?: string; ascending?: boolean; limit?: number; offset?: number; single?: boolean; allowEmpty?: boolean } = {}
  ): Promise<{ data: T[] | T | null; error: any }> {
    try {
      // Get the REST API endpoint for this table
      const endpoint = this.tableEndpointMap[table] || table;
      
      const queryParams = new URLSearchParams();

      if (columns !== '*') {
        queryParams.append('select', columns);
      }

      // Add filters to query params with proper REST API parameter mapping
      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === 'object' && value !== null) {
          queryParams.append(key, JSON.stringify(value));
        } else {
          // Convert column names to REST API format
          const restApiKey = this.convertColumnToRestApi(key);
          queryParams.append(restApiKey, String(value));
        }
      }

      // Convert pagination parameters to REST API format
      if (options.limit) {
        queryParams.append('limit', String(options.limit));
      }
      if (options.offset) {
        queryParams.append('offset', String(options.offset));
      }

      // Convert sorting parameters to REST API format
      if (options.orderBy) {
        queryParams.append('sort_by', options.orderBy);
        queryParams.append('order', options.ascending !== false ? 'asc' : 'desc');
      }

      // Special handling for contacts - requires business_account_id
      if (table === 'contacts' && filters.business_account_id) {
        queryParams.append('business_account_id', String(filters.business_account_id));
      }

      // Special handling for conversations and messages
      if ((table === 'conversations' || table === 'messages') && filters.businessAccountId) {
        queryParams.append('businessAccountId', String(filters.businessAccountId));
      }

      // Special handling for WhatsApp templates
      if (table === 'whatsapp_templates' && filters.business_account_id) {
        queryParams.append('business_account_id', String(filters.business_account_id));
      }

      // Special handling for campaigns
      if (table === 'campaigns' && filters.business_account_id) {
        queryParams.append('business_account_id', String(filters.business_account_id));
      }

      const endpointUrl = `/${endpoint}?${queryParams.toString()}`;
      const result = await this.apiRequest<T[]>(endpointUrl);

      if (result.error) {
        return { data: null, error: result.error };
      }

      if (options.single) {
        if (!result.data || result.data.length === 0) {
          return { data: options.allowEmpty ? null : null, error: options.allowEmpty ? null : new Error('No rows returned') };
        }
        return { data: result.data[0], error: null };
      }

      return { data: result.data || [], error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper method to convert column names to REST API format
  private convertColumnToRestApi(column: string): string {
    const columnMap: Record<string, string> = {
      'business_account_id': 'business_account_id',
      'businessAccountId': 'businessAccountId',
      'created_at': 'created_at',
      'updated_at': 'updated_at',
      'phone_number': 'phone_number',
      'custom_fields': 'custom_fields',
      'profile_picture_url': 'profile_picture_url',
      'last_seen': 'last_seen',
      'tenant_id': 'tenant_id',
    };
    
    return columnMap[column] || column;
  }

  // Helper method to insert data
  private async insertData<T>(table: string, data: Partial<T> | Partial<T>[]): Promise<{ data: T[] | null; error: any }> {
    try {
      // Get the REST API endpoint for this table
      const endpoint = this.tableEndpointMap[table] || table;
      
      const result = await this.apiRequest<T[]>(`/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });

      return result;
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper method to insert and select
  private async insertAndSelect<T>(table: string, data: Partial<T> | Partial<T>[], columns: string): Promise<{ data: T[] | null; error: any }> {
    try {
      await this.insertData<T>(table, data);
      const result = await this.buildSelectQuery<T>(table, columns, {});
      return { data: result.data as T[], error: result.error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper method to build UPDATE queries
  private async buildUpdateQuery<T>(
    table: string,
    data: Partial<T>,
    filters: Record<string, any> = {},
    selectColumns?: string
  ): Promise<{ data: T[] | null; error: any }> {
    try {
      // Get the REST API endpoint for this table
      const endpoint = this.tableEndpointMap[table] || table;
      
      // Check if we have an 'id' filter for the new endpoint format
      if (filters.id) {
        const endpointUrl = `/${endpoint}/${filters.id}`;
        const result = await this.apiRequest(endpointUrl, {
          method: 'PUT',
          body: JSON.stringify(data),
        });

        if (result.error) {
          return { data: null, error: result.error };
        }

        // If selectColumns is specified, fetch the updated data
        if (selectColumns) {
          const selectResult = await this.buildSelectQuery<T>(table, selectColumns, filters);
          return { data: selectResult.data as T[], error: selectResult.error };
        }

        return { data: result.data as T[], error: null };
      }

      // Fallback to old format with query params for other filters
      const queryParams = new URLSearchParams();

      // Add filters to query params
      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === 'object' && value !== null) {
          queryParams.append(key, JSON.stringify(value));
        } else {
          queryParams.append(key, String(value));
        }
      }

      const endpointUrl = `/${endpoint}?${queryParams.toString()}`;
      const result = await this.apiRequest(endpointUrl, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (result.error) {
        return { data: null, error: result.error };
      }

      return { data: result.data as T[], error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper method to update data
  private async updateData<T>(table: string, data: Partial<T>, filters: Record<string, any>): Promise<{ data: T[] | null; error: any }> {
    return this.buildUpdateQuery<T>(table, data, filters);
  }

  // Helper method to build DELETE queries
  private async buildDeleteQuery<T>(table: string, filters: Record<string, any> = {}): Promise<{ data: T[] | null; error: any }> {
    try {
      // Get the REST API endpoint for this table
      const endpoint = this.tableEndpointMap[table] || table;
      
      // Check if we have an 'id' filter for the new endpoint format
      if (filters.id) {
        const endpointUrl = `/${endpoint}/${filters.id}`;
        const result = await this.apiRequest(endpointUrl, {
          method: 'DELETE',
        });
        return { data: result.data as T[], error: result.error };
      }

      // Fallback to old format with query params for other filters
      const queryParams = new URLSearchParams();

      // Add filters to query params
      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === 'object' && value !== null) {
          queryParams.append(key, JSON.stringify(value));
        } else {
          queryParams.append(key, String(value));
        }
      }

      const endpointUrl = `/${endpoint}?${queryParams.toString()}`;
      const result = await this.apiRequest(endpointUrl, {
        method: 'DELETE',
      });

      return { data: result.data as T[], error: result.error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper method to delete data
  private async deleteData<T>(table: string, filters: Record<string, any>): Promise<{ data: T[] | null; error: any }> {
    return this.buildDeleteQuery<T>(table, filters);
  }

  // Storage operations (simplified for MySQL)
  storage = {
    from: (bucket: string) => ({
      upload: async (path: string, file: File | Blob, options: any = {}) => {
        // For now, return a mock response
        // In a real implementation, you'd handle file storage
        return {
          data: {
            path,
            id: Math.random().toString(36).substr(2, 9),
            fullPath: `${bucket}/${path}`,
            name: path.split('/').pop(),
            bucket_id: bucket,
            owner: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_accessed_at: new Date().toISOString(),
            metadata: {}
          },
          error: null
        };
      },

      download: async (path: string) => {
        // Mock implementation
        return {
          data: new Blob(['mock file content']),
          error: null
        };
      },

      remove: async (paths: string[]) => {
        // Mock implementation
        return {
          data: paths.map(path => ({ path })),
          error: null
        };
      },

      getPublicUrl: (path: string) => {
        // Mock implementation
        return {
          data: {
            publicUrl: `https://example.com/storage/v1/object/public/${bucket}/${path}`
          }
        };
      }
    })
  };



  // Real-time subscriptions (simplified for MySQL)
  channel = (channelName: string) => {
    const channelObj = {
      on: (event: string, config: any, callback: (payload: any) => void) => {
        return channelObj; // Return self for chaining
      },
      subscribe: (callback: (status: string, err?: any) => void) => {
        // Simulate successful subscription
        setTimeout(() => callback('SUBSCRIBED'), 100);
        return {
          unsubscribe: () => {
          }
        };
      }
    };
    return channelObj;
  };

  // Remove channel method
  removeChannel = (channel: any) => {
    if (channel && channel.unsubscribe) {
      channel.unsubscribe();
    }
  };

  // Close the connection (no-op for API client)
  async close() {
    // No-op for API client
  }
}

// Create and export the MySQL client instance
export const mysqlClient = new MySQLClient();

// Export types for compatibility
export type Database = {
  public: {
    Tables: Record<string, {
      Row: any;
      Insert: any;
      Update: any;
    }>;
  };
};

// For backward compatibility, export as 'supabase'
export const supabase = mysqlClient;